import React from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type DemographicTargeting = {
    id: string; // e.g. "AGE_18_24", "GENDER_MALE"
    label: string; // e.g. "18〜24歳", "男性"
    before_bid_modifier: number | null; // null: 100%(ON), -1.0: 0%(OFF)
    suggested_bid_modifier: number | null;
    reason?: string;
};

export type DemographicSuggestionGroup = {
    campaign_id: string;
    campaign_name: string;
    ad_group_id?: string;
    ad_group_name?: string;
    items: DemographicTargeting[];
};

type Props = {
    groups: DemographicSuggestionGroup[];
    formData: Record<string, number | null>; // id -> modifier
    onChange: (id: string, val: number | null) => void;
};

const normalizeToggleValue = (value: number | null | undefined): number | null => {
    const n = Number(value);
    if (Number.isFinite(n) && n <= -0.999) return -1.0; // OFF
    return null; // ON (null / 0.0 / undefined を含む)
};

// -----------------------------------------------------------------------------
// Component: TargetingDemographicForm
// -----------------------------------------------------------------------------
const TargetingDemographicForm: React.FC<Props> = ({ groups, formData, onChange }) => {
    return (
        <div className={styles.targetingDeviceFormContainer}>
            {groups.map((group) => (
                <section key={group.ad_group_id || group.campaign_id} className={styles.campaignCard}>
                    <div className={styles.campaignName}>
                        {group.ad_group_name ? `${group.campaign_name} / ${group.ad_group_name}` : group.campaign_name}
                    </div>

                    {/* Render each demographic item in the campaign as its own row */}
                    {group.items.map(item => {
                        const beforeNormalized = normalizeToggleValue(item.before_bid_modifier);
                        const currentModifier = formData[item.id] !== undefined ? formData[item.id] : item.suggested_bid_modifier;
                        const afterNormalized = normalizeToggleValue(currentModifier);
                        const isBeforeEnabled = beforeNormalized === null;
                        const isAfterEnabled = afterNormalized === null;

                        return (
                            <div key={item.id} className={styles.demographicItemWrap}>
                                {item.reason && (
                                    <div className={styles.reasonBar}>
                                        <strong>Aibex提案理由:</strong> {item.reason}
                                    </div>
                                )}
                                <div className={styles.suggestionBox}>
                                    {/* Before */}
                                    <div className={styles.beforePanel}>
                                        <div className={styles.panelTitle}>{item.label} (現在)</div>
                                        <div className={styles.displayRow}>
                                            <div className={`${styles.statusBadge} ${isBeforeEnabled ? styles.bgOn : styles.bgOff}`}>
                                                {isBeforeEnabled ? "配信 ON" : "配信 OFF"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Arrow */}
                                    <div className={styles.arrowIcon}>→</div>

                                    {/* After */}
                                    <div className={styles.afterPanel}>
                                        <div className={styles.panelTitle}>AI提案</div>
                                        <div className={styles.inputRow}>
                                            <label className={styles.checkboxSwitch}>
                                                <input
                                                    type="checkbox"
                                                    checked={isAfterEnabled}
                                                    onChange={(e) => onChange(item.id, e.target.checked ? null : -1.0)}
                                                />
                                                <span className={styles.slider}></span>
                                            </label>
                                            <div className={`${styles.statusBadge} ${isAfterEnabled ? styles.bgOn : styles.bgOff}`}>
                                                {isAfterEnabled ? "配信 ON" : "配信 OFF"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </section>
            ))}
        </div>
    );
};

export default TargetingDemographicForm;
